/**
 * =============================================================================
 * IDS CRM Web - Support Configuration Settings
 * =============================================================================
 *
 * PURPOSE:
 * Configurable settings for the Scheduled & Ticketed Support system.
 * Currently manages Fault Categories with add/edit/delete functionality.
 *
 * =============================================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';

const slugify = (text) => String(text || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const CONFIG_SECTIONS = [
  ['reactive_ticket_statuses', 'Reactive Ticket statuses', false],
  ['scheduled_work_statuses', 'Scheduled Work statuses', false],
  ['scheduled_maintenance_statuses', 'Scheduled Maintenance statuses', false],
  ['support_tiers', 'Support tiers', false],
  ['comms_channels', 'Comms channels', false],
  ['assignment_reasons', 'Assignment reasons', false],
  ['work_log_types', 'Work log types', false],
  ['work_periods', 'Work Periods', false],
  ['work_outcomes', 'Work outcomes', true],
  ['commercial_comms_methods', 'Commercial comms methods', false],
  ['commercial_activity_types', 'Commercial activity types', false],
  ['commercial_activity_outcomes', 'Commercial activity outcomes', false]
];

function SupportConfigSettings() {
  const { authFetch, user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [configLists, setConfigLists] = useState({});
  const [safeValues, setSafeValues] = useState({});
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ value: '', label: '', sort_order: 0, asset_types: [] });
  const [saving, setSaving] = useState(false);

  // Mirrors the API's authorize(ROLES.OPS) on config-lists, fault-category
  // and SLA-matrix writes (Gareth 2026-08-11: managers and team leaders run
  // this tab, not just read it).
  const canEdit = ['admin', 'manager', 'team_leader'].includes(user?.role);
  const { confirmElement, requestConfirm } = useConfirm();

  // Batch E: configurable SLA matrix (defaults + priority floors)
  const [slaMatrix, setSlaMatrix] = useState(null);
  const [slaSaving, setSlaSaving] = useState(false);

  const fetchSlaMatrix = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/admin/support-settings/sla-matrix');
      if (response.ok) {
        const body = await response.json();
        setSlaMatrix(body.data);
      }
    } catch (error) {
      console.error('Failed to fetch SLA matrix:', error);
    }
  }, [authFetch]);

  useEffect(() => { fetchSlaMatrix(); }, [fetchSlaMatrix]);

  const saveSlaMatrix = async () => {
    setSlaSaving(true);
    try {
      const response = await authFetch('/api/v1/admin/support-settings/sla-matrix', {
        method: 'PUT',
        body: JSON.stringify(slaMatrix)
      });
      if (response.ok) {
        setMessage({ text: 'SLA matrix saved', type: 'success' });
      } else {
        const body = await response.json().catch(() => ({}));
        setMessage({ text: body.message || 'Failed to save SLA matrix', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Failed to save SLA matrix', type: 'error' });
    } finally {
      setSlaSaving(false);
    }
  };

  const setSlaField = (field, value) => {
    setSlaMatrix(prev => ({ ...prev, [field]: value }));
  };

  const setSlaFloor = (priority, field, value) => {
    setSlaMatrix(prev => ({
      ...prev,
      priority_floors: {
        ...prev.priority_floors,
        [priority]: { ...(prev.priority_floors?.[priority] || {}), [field]: value }
      }
    }));
  };

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [categoryResponse, configResponse, assetConfigResponse] = await Promise.all([
        authFetch('/api/v1/admin/fault-categories'),
        authFetch('/api/v1/admin/support-settings/config-lists'),
        authFetch('/api/v1/admin/asset-config')
      ]);

      if (!categoryResponse.ok) throw new Error('Failed to load fault categories');
      if (!configResponse.ok) throw new Error('Failed to load support configuration');

      const categoryData = await categoryResponse.json();
      const configData = await configResponse.json();
      const assetConfigData = assetConfigResponse.ok ? await assetConfigResponse.json() : { data: { asset_types: [] } };
      setCategories(categoryData.data || []);
      setSafeValues(configData.meta?.safe_values || {});
      setConfigLists(CONFIG_SECTIONS.reduce((acc, [key]) => ({
        ...acc,
        [key]: configData.data?.[key] || []
      }), {}));
      setAssetTypes(assetConfigData.data?.asset_types || []);
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const resetForm = () => {
    setFormData({ value: '', label: '', sort_order: 0, asset_types: [] });
    setEditingCategory(null);
  };

  const openAddModal = () => {
    resetForm();
    const maxSort = categories.reduce((max, c) => Math.max(max, c.sort_order || 0), 0);
    setFormData({ value: '', label: '', sort_order: maxSort + 10, asset_types: [] });
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      value: category.value,
      label: category.label,
      sort_order: category.sort_order || 0,
      asset_types: category.asset_types || []
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      if (editingCategory) {
        // Update existing
        const response = await authFetch(`/api/v1/admin/fault-categories/${editingCategory.category_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: formData.label,
            sort_order: parseInt(formData.sort_order, 10) || 0,
            asset_types: formData.asset_types
          })
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to update category');
        }
        setMessage({ text: 'Category updated', type: 'success' });
      } else {
        // Create new
        const response = await authFetch('/api/v1/admin/fault-categories', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            value: formData.value.toLowerCase().replace(/[^a-z0-9]/g, '_'),
            label: formData.label,
            sort_order: parseInt(formData.sort_order, 10) || 0,
            asset_types: formData.asset_types
          })
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to create category');
        }
        setMessage({ text: 'Category created', type: 'success' });
      }
      closeModal();
      fetchSettings();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      const response = await authFetch(`/api/v1/admin/fault-categories/${category.category_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !category.active })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update category');
      }
      setMessage({ text: `Category ${category.active ? 'deactivated' : 'activated'}`, type: 'success' });
      fetchSettings();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    }
  };

  const handleDelete = (category) => {
    requestConfirm({
      title: 'Delete fault category?',
      message: `Delete "${category.label}"? If it's in use by tickets, it will be deactivated instead.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => performDelete(category)
    });
  };

  const performDelete = async (category) => {
    try {
      const response = await authFetch(`/api/v1/admin/fault-categories/${category.category_id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete category');
      }
      setMessage({ text: data.message, type: 'success' });
      fetchSettings();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    }
  };

  const toggleCategoryAssetType = (assetTypeValue) => {
    setFormData(prev => {
      const current = prev.asset_types || [];
      return {
        ...prev,
        asset_types: current.includes(assetTypeValue)
          ? current.filter(value => value !== assetTypeValue)
          : [...current, assetTypeValue]
      };
    });
  };

  const updateListItem = (key, index, patch) => {
    setConfigLists(prev => ({
      ...prev,
      [key]: prev[key].map((item, i) => (i === index ? { ...item, ...patch } : item))
    }));
  };

  const moveListItem = (key, index, delta) => {
    setConfigLists(prev => {
      const items = [...prev[key]];
      const target = index + delta;
      if (target < 0 || target >= items.length) return prev;
      [items[index], items[target]] = [items[target], items[index]];
      return { ...prev, [key]: items };
    });
  };

  const removeListItem = (key, index) => {
    setConfigLists(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));
  };

  const addListItem = (key, includeCloses) => {
    setConfigLists(prev => ({
      ...prev,
      [key]: [
        ...prev[key],
        { label: '', value: '', active: true, _new: true, ...(includeCloses ? { closes_work: false } : {}) }
      ]
    }));
  };

  const saveConfigLists = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authFetch('/api/v1/admin/support-settings/config-lists', {
        method: 'PUT',
        body: JSON.stringify(CONFIG_SECTIONS.reduce((acc, [key]) => ({
          ...acc,
          // Row order is the sort order; blank rows are dropped
          [key]: (configLists[key] || [])
            .filter(item => String(item.label || '').trim() && String(item.value || '').trim())
            .map(({ _new, ...item }, index) => ({ ...item, sort_order: index * 10 }))
        }), {}))
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save support configuration');
      setMessage({ text: 'Support configuration saved', type: 'success' });
      fetchSettings();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading support configuration...</div>;
  }

  return (
    <div className="support-config-settings">
      {confirmElement}
      {message.text && (
        <div className={`admin-message ${message.type}`}>{message.text}</div>
      )}

      {slaMatrix && (
        <div className="admin-section">
          <div className="section-header">
            <h2>SLA Matrix</h2>
            <div className="section-actions">
              <p className="section-hint">
                Default windows apply when a customer has no contracted SLA; priority floors tighten
                whichever applies. Used by ticket creation and maintenance follow-ups.
              </p>
            </div>
          </div>
          <div className="sla-matrix-grid">
            <div className="sla-matrix-field">
              <label>Default response (hrs)</label>
              <input type="number" min="0.25" step="0.25" value={slaMatrix.default_response_hrs}
                disabled={!canEdit}
                onChange={(e) => setSlaField('default_response_hrs', e.target.value)} />
            </div>
            <div className="sla-matrix-field">
              <label>Default resolution (hrs)</label>
              <input type="number" min="0.25" step="0.25" value={slaMatrix.default_resolution_hrs}
                disabled={!canEdit}
                onChange={(e) => setSlaField('default_resolution_hrs', e.target.value)} />
            </div>
            {['critical', 'high'].map(priority => (
              <React.Fragment key={priority}>
                <div className="sla-matrix-field">
                  <label>{priority} floor — response (hrs)</label>
                  <input type="number" min="0.25" step="0.25"
                    value={slaMatrix.priority_floors?.[priority]?.response ?? ''}
                    disabled={!canEdit}
                    onChange={(e) => setSlaFloor(priority, 'response', e.target.value)} />
                </div>
                <div className="sla-matrix-field">
                  <label>{priority} floor — resolution (hrs)</label>
                  <input type="number" min="0.25" step="0.25"
                    value={slaMatrix.priority_floors?.[priority]?.resolution ?? ''}
                    disabled={!canEdit}
                    onChange={(e) => setSlaFloor(priority, 'resolution', e.target.value)} />
                </div>
              </React.Fragment>
            ))}
            <div className="sla-matrix-field">
              <label>Reopen window (days)</label>
              <input type="number" min="1" step="1" value={slaMatrix.reopen_window_days ?? 14}
                disabled={!canEdit}
                title="How long after closure a ticket can be reopened; after this, raise a linked follow-on ticket instead"
                onChange={(e) => setSlaField('reopen_window_days', e.target.value)} />
            </div>
          </div>
          {canEdit && (
            <button className="btn-primary" onClick={saveSlaMatrix} disabled={slaSaving}>
              {slaSaving ? 'Saving...' : 'Save SLA Matrix'}
            </button>
          )}
        </div>
      )}

      <div className="admin-section">
        <div className="section-header">
          <h2>Configurable Support Lists</h2>
          <div className="section-actions">
            <p className="section-hint">
              Row order sets dropdown order. Locked rows are core workflow values — rename them,
              but they can't be deleted or deactivated.
            </p>
            {canEdit && (
              <button className="btn-primary" onClick={saveConfigLists} disabled={saving}>
                {saving ? 'Saving...' : 'Save Lists'}
              </button>
            )}
          </div>
        </div>
        <div className="config-list-grid">
          {CONFIG_SECTIONS.map(([key, label, includeCloses]) => {
            const items = configLists[key] || [];
            const fixedSet = Array.isArray(safeValues[key]);
            return (
              <div className={`config-list-editor${includeCloses ? ' config-list-wide' : ''}`} key={key}>
                <label className="config-list-title">{label}</label>
                {fixedSet && (
                  <span className="field-hint">
                    Fixed set — supported values: {safeValues[key].join(', ')}
                  </span>
                )}
                <div className="config-table-container">
                  <table className="config-table">
                    <thead>
                      <tr>
                        <th>Label</th>
                        <th>Key</th>
                        {includeCloses && <th>On save</th>}
                        <th>Active</th>
                        {canEdit && <th aria-label="Actions" />}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={item._new ? `new-${index}` : item.value} className={item.active === false ? 'inactive-row' : ''}>
                          <td>
                            <input
                              type="text"
                              className="config-list-input"
                              value={item.label}
                              placeholder="Label"
                              disabled={!canEdit}
                              onChange={e => updateListItem(key, index, {
                                label: e.target.value,
                                ...(item._new ? { value: slugify(e.target.value) } : {})
                              })}
                            />
                          </td>
                          <td className="value-cell">
                            {item._new ? (
                              <input
                                type="text"
                                className="config-list-input config-list-key"
                                value={item.value}
                                placeholder="system_key"
                                disabled={!canEdit}
                                onChange={e => updateListItem(key, index, { value: slugify(e.target.value) })}
                              />
                            ) : (
                              <code>{item.value}</code>
                            )}
                            {item.protected && (
                              <span
                                className="protected-lock"
                                title="Core value used by the workflow engine — label editable; cannot be deleted or deactivated."
                                role="img"
                                aria-label="Protected"
                              >🔒</span>
                            )}
                          </td>
                          {includeCloses && (
                            <td>
                              <button
                                type="button"
                                className={`status-toggle ${item.closes_work ? 'active' : 'inactive'}`}
                                disabled={!canEdit || item.protected}
                                title={item.protected ? 'Core closing outcome' : 'Click to switch'}
                                onClick={() => updateListItem(key, index, { closes_work: !item.closes_work })}
                              >
                                {item.closes_work ? 'Closes work' : 'Hands over'}
                              </button>
                            </td>
                          )}
                          <td className="status-cell">
                            <button
                              type="button"
                              className={`status-toggle ${item.active !== false ? 'active' : 'inactive'}`}
                              disabled={!canEdit || item.protected}
                              onClick={() => updateListItem(key, index, { active: item.active === false })}
                            >
                              {item.active !== false ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          {canEdit && (
                            <td className="actions-cell">
                              <button type="button" className="btn-icon" title="Move up" disabled={index === 0}
                                onClick={() => moveListItem(key, index, -1)}>↑</button>
                              <button type="button" className="btn-icon" title="Move down" disabled={index === items.length - 1}
                                onClick={() => moveListItem(key, index, 1)}>↓</button>
                              {!item.protected && (
                                <button type="button" className="btn-icon delete" title="Remove"
                                  onClick={() => removeListItem(key, index)}>×</button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                      {items.length === 0 && (
                        <tr>
                          <td colSpan={canEdit ? (includeCloses ? 5 : 4) : (includeCloses ? 4 : 3)} className="empty-row">
                            No values configured
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {canEdit && (
                  <button type="button" className="btn-secondary config-list-add" onClick={() => addListItem(key, includeCloses)}>
                    + Add value
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="admin-section">
        <div className="section-header">
          <h2>Fault Categories</h2>
          <div className="section-actions">
            <p className="section-hint">Categories used when logging support tickets</p>
            {canEdit && (
              <button className="btn-primary" onClick={openAddModal}>
                + Add Category
              </button>
            )}
          </div>
        </div>

        <div className="config-table-container">
          <table className="config-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Value</th>
                <th>Asset types</th>
                <th>Sort Order</th>
                <th>Status</th>
                {canEdit && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {categories.map(category => (
                <tr key={category.category_id} className={category.active ? '' : 'inactive-row'}>
                  <td className="label-cell">{category.label}</td>
                  <td className="value-cell">
                    <code>{category.value}</code>
                  </td>
                  <td>{(category.asset_types || []).length === 0 ? 'All' : category.asset_types.join(', ')}</td>
                  <td className="sort-cell">{category.sort_order}</td>
                  <td className="status-cell">
                    {canEdit ? (
                      <button
                        className={`status-toggle ${category.active ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleActive(category)}
                        title={category.active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {category.active ? 'Active' : 'Inactive'}
                      </button>
                    ) : (
                      <span className={`status-badge ${category.active ? 'active' : 'inactive'}`}>
                        {category.active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="actions-cell">
                      <button className="btn-icon" onClick={() => openEditModal(category)} title="Edit">
                        Edit
                      </button>
                      <button className="btn-icon delete" onClick={() => handleDelete(category)} title="Delete">
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {categories.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 6 : 5} className="empty-row">
                    No fault categories configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCategory ? 'Edit Fault Category' : 'Add Fault Category'}</h3>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {!editingCategory && (
                  <div className="form-group">
                    <label htmlFor="value">Value (system key)</label>
                    <input
                      id="value"
                      type="text"
                      value={formData.value}
                      onChange={e => setFormData({ ...formData, value: e.target.value })}
                      placeholder="e.g., hydraulic_fault"
                      pattern="[a-zA-Z][a-zA-Z0-9_]*"
                      required
                    />
                    <span className="field-hint">Lowercase letters, numbers, underscores. Cannot be changed after creation.</span>
                  </div>
                )}
                {editingCategory && (
                  <div className="form-group">
                    <label>Value (system key)</label>
                    <code className="readonly-value">{formData.value}</code>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="label">Label (display name)</label>
                  <input
                    id="label"
                    type="text"
                    value={formData.label}
                    onChange={e => setFormData({ ...formData, label: e.target.value })}
                    placeholder="e.g., Hydraulic Fault"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="sort_order">Sort Order</label>
                  <input
                    id="sort_order"
                    type="number"
                    min="0"
                    value={formData.sort_order}
                    onChange={e => setFormData({ ...formData, sort_order: e.target.value })}
                  />
                  <span className="field-hint">Lower numbers appear first in dropdowns</span>
                </div>
                <div className="form-group">
                  <label>Asset/product types</label>
                  <span className="field-hint">Leave all unchecked to show this category for all asset types.</span>
                  <div className="owner-group-grid">
                    {assetTypes.map(type => (
                      <label key={type.value} className={`owner-group-option ${(formData.asset_types || []).includes(type.value) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={(formData.asset_types || []).includes(type.value)}
                          onChange={() => toggleCategoryAssetType(type.value)}
                        />
                        <span>
                          <strong>{type.label}</strong>
                          <small>{type.value}</small>
                        </span>
                      </label>
                    ))}
                    {assetTypes.length === 0 && <p className="empty-row">No asset types configured</p>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingCategory ? 'Save Changes' : 'Add Category')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportConfigSettings;
